<template>
    <div class="user-details" :class="classObject">
        <slot />
        <div class="user-name">
            <p>{{ user.name }}</p>
            <router-link
                v-if="hasEditLink"
                to="/settings"
                class="edit-account-link"
            >
                <i class="fas fa-pencil" /> Edit Account
            </router-link>
        </div>
        <div
            class="user-plans flex"
            :class="{'with-dropdown': 1 < subscriptions.length}"
        >
            <div class="user-plans-list">
                <span>Your Plan:</span>
                <span class="user-plan">
                    <template v-if="1 < subscriptions.length">
                        <subscription-dropdown />
                    </template>
                    <template v-else-if="user.pro">
                        {{ planName }}
                    </template>
                    <template v-else>
                        Fusebox Free
                    </template>
                </span>
            </div>
            <div
                v-if="user.pro"
                class="user-plans-managment"
                :class="{'with-dropdown': 1 < subscriptions.length}"
            >
                <span>Active Sites:</span>
                <div class="user-sites-counter" :class="{'is-exceeded': isExceeded}">
                    <span class="active">{{ usedSeats }}</span> / <span class="total">{{ allSeats }}</span>
                    <template v-if="'lifetime' !== currentSubscription.name">
                        <router-link to="/settings/sites">
                            (Manage Sites)
                        </router-link>
                    </template>
                    <template v-else>
                        <router-link :to="`/settings/billing/lifetime/${currentSubscription.id}`">
                            (Fusebox Lifetime)
                        </router-link>
                    </template>
                </div>
            </div>
            <div v-else-if="hasUpgradeButton" class="upgrade-button-wrap">
                <a
                    href="#"
                    class="btn is-green is-small upgrade-button"
                    @click.prevent="openUpgradeModal"
                >Upgrade to Pro Now</a>
            </div>
        </div>
    </div>
</template>

<script>
import SubscriptionDropdown from './Dashboard/SubscriptionDropdown.vue';
import {
    mapState,
    mapGetters,
    mapMutations,
} from 'vuex';
import { filter } from 'lodash';

export default {
    components: {
        SubscriptionDropdown,
    },

    props: {
        hasEditLink: {
            type: Boolean,
            default: false,
        },

        hasUpgradeButton: {
            type: Boolean,
            default: false,
        },

        layout: {
            type: String,
            default: 'small',
        },
    },

    computed: {
        ...mapState(['user', 'currentSubscription']),
        ...mapGetters(['totalLicenseSeats', 'licensesForCurrentPlan']),

        /**
         * Filtered active subscriptions array.
         *
         * @returns {Array} Active subscriptions.
         */
        subscriptions() {
            return filter(this.user.subscriptions, (subscription) => {
                const status = subscription.stripe_status;

                return 'active' === status || 'past_due' === status;
            });
        },

        /**
         * Return style class.
         *
         * @returns {string} Class name.
         */
        classObject() {
            return {
                [`layout-${this.layout}`]: true,
                'is-pro': this.user.pro,
            };
        },

        /**
         * @returns {number|string} Used license seats.
         */
        usedSeats() {
            return this.totalLicenseSeats ? this.totalLicenseSeats.used : '-';
        },

        /**
         * @returns {number|string} All license seats.
         */
        allSeats() {
            return this.totalLicenseSeats ? this.totalLicenseSeats.all : '-';
        },

        /**
         * @returns {boolean} Whether the license seats number is exceeded.
         */
        isExceeded() {
            return this.totalLicenseSeats.used > this.totalLicenseSeats.all;
        },

        /**
         * Return plan name based on subscription type (regular pro subscription or lifetime).
         *
         * @returns {string} Plan name.
         */
        planName() {
            if (! this.currentSubscription) {
                return '-';
            }

            return this.currentSubscription && 'lifetime' === this.currentSubscription.name
                ? 'Fusebox Pro Lifetime' : 'Fusebox Pro';
        },
    },

    methods: {
        ...mapMutations(['changeUIState']),

        /**
         * Opens upgrade modal
         *
         */
        openUpgradeModal() {
            this.changeUIState({ isUpgradeModalVisible: true });
        },
    },
};
</script>
